import { useEffect } from "react";
import { Field, FieldArray, Fields } from "redux-form";

import { Box, Typography } from "@braintrust/braintrust-ui-components";
import { FormLayoutWrapper } from "@js/apps/common/components";
import { useWithRecaptcha } from "@js/apps/common/hooks";
import { useUser } from "@js/apps/common/hooks/use-user";
import {
  AvailabilityFieldContainer,
  BidRateSection,
  CalendarField,
  FieldSectionPanelWithImportance,
  NewApplicationAnswersField,
} from "@js/apps/jobs/forms/fields";
import { AddResumeToBidField } from "@js/apps/jobs/forms/fields/resume/add-resume-to-bid";
import { requiredWithSpecificMessage } from "@js/forms/validators";
import type { OverwriteContentComponentProps } from "@js/layouts/app";
import { AppLayout } from "@js/layouts/app";
import {
  ConversionEvents,
  pushConversionEvent,
} from "@js/services/analytics/conversion-tracking";
import type { BidAttachment, FreelancerBidParams, Job } from "@js/types/jobs";

import {
  BidErrorSection,
  JobBidHeaderPortal,
} from "../../apps/bids/components";
import { useEditBid } from "../../apps/bids/hooks/edit-bid";
import {
  useCreateBid,
  useCreateBidPublic,
} from "../../apps/bids/hooks/new-bid";
import type {
  FreelancerBidFormValues,
  PublicFreelancerBidFormValues,
} from "../../apps/bids/utils/bid-form";
import {
  BudgetDetail,
  DeadlineDetail,
  LocationDetail,
  LocationRequirementWarning,
  TimezoneDetail,
  WorkTimeDetail,
} from "../../components";
import { PublicBidUserInfoSection } from "../../components/public-bid-user-info-section";
import { JobNewBidFileDropField } from "../../forms/fields/job-new-bid-file-drop-field";
import { LocationAndTimezoneRequirements } from "../../forms/fields/location-timezone-requirements";
import { WorkAuthorizationField } from "../../forms/fields/work-authorization-field";

import style from "./style.module.scss";

export const JobNewBidPage = () => {
  return (
    <AppLayout
      pageTitle="Apply to job"
      bgcolor="var(--off-white)"
      className="overflow-initial"
      disableSubheaderPortal
      hideMenu
      OverwriteContentComponent={JobNewBidPageContentWrapped}
      children={null}
    />
  );
};

const JobNewBidPageContent = (props: OverwriteContentComponentProps) => {
  const { job, loading, ...formProps } = useCreateBid();

  useEffect(() => {
    if (!job || job.can_bid === false) {
      return;
    }
    pushConversionEvent(ConversionEvents.APPLICATION_START);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  return (
    <FormLayoutWrapper<FreelancerBidFormValues>
      formId="job-new-bid-form"
      formProps={formProps}
      loading={loading || !job}
      aboveContentFullWidthSocket={<DetailsSubheader job={job} />}
      {...props}
    >
      {job && (
        <>
          <JobBidHeaderPortal job={job} />
          <LocationRequirementWarning
            sx={{ mb: 4 }}
            job={{
              ...job,
              strong_location_requirement_met:
                job.strong_location_requirement_met,
              strong_timezone_requirement_met:
                job.strong_timezone_requirement_met,
            }}
          />
        </>
      )}
      <JobBidFormFields job={job} />
    </FormLayoutWrapper>
  );
};

const JobNewBidPublicPageContent = (props: OverwriteContentComponentProps) => {
  useWithRecaptcha();
  const { job, loading, ...formProps } = useCreateBidPublic();

  useEffect(() => {
    if (!job || job.can_bid === false) {
      return;
    }
    pushConversionEvent(ConversionEvents.APPLICATION_START);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  return (
    <FormLayoutWrapper<PublicFreelancerBidFormValues>
      formId="job-new-bid-form"
      formProps={formProps}
      loading={loading || !job}
      aboveContentFullWidthSocket={<DetailsSubheader job={job} />}
      {...props}
    >
      {job && <JobBidHeaderPortal job={job} isPublicBidForm />}
      <JobBidFormFields job={job} isPublicBidForm />
    </FormLayoutWrapper>
  );
};

const JobNewBidPageContentWrapped = (props: OverwriteContentComponentProps) => {
  const user = useUser();
  if (!user) {
    return <JobNewBidPublicPageContent {...props} />;
  }

  return <JobNewBidPageContent {...props} />;
};

export const JobEditBidPage = () => {
  return (
    <AppLayout
      pageTitle="Edit proposal"
      bgcolor="var(--off-white)"
      disableSubheaderPortal
      hideMenu
      OverwriteContentComponent={JobEditBidPageContent}
      children={null}
    />
  );
};

const JobEditBidPageContent = (props: OverwriteContentComponentProps) => {
  const { job, bid, loading, attachments, ...formProps } = useEditBid();

  return (
    <FormLayoutWrapper<FreelancerBidParams>
      formId="job-edit-bid-form"
      formProps={formProps}
      loading={loading || !job}
      aboveContentFullWidthSocket={<DetailsSubheader job={job} />}
      {...props}
    >
      {job && (
        <JobBidHeaderPortal job={job} isEditPage canEdit={bid?.can_edit} />
      )}
      <JobBidFormFields job={job} existing={attachments} isEditPage />
    </FormLayoutWrapper>
  );
};

type JobBidFormProps = {
  job: Job | null | undefined;
  isEditPage?: boolean;
  existing?: Array<BidAttachment> | undefined;
  isPublicBidForm?: boolean;
};

const thisFieldIsRequired = requiredWithSpecificMessage(
  "This field is required.",
);

const JobBidFormFields = ({
  job,
  existing,
  isEditPage,
  isPublicBidForm,
}: JobBidFormProps) => {
  if (!job) {
    return null;
  }

  const isResumeRequired = job?.is_resume_required || !!isPublicBidForm;

  return (
    <>
      <Typography
        component="h1"
        variant="title"
        fontWeight={400}
        sx={{ mb: 4 }}
      >
        Fill out your application
      </Typography>
      <div className={style.errorSection}>
        <Field
          name="_error_section_field"
          job={job}
          component={BidErrorSection}
          isPublicBidForm={isPublicBidForm}
        />
      </div>
      <div className={style.jobBidForm}>
        <div>
          {isPublicBidForm && <PublicBidUserInfoSection />}
          <BidRateSection job={job} />
          <LocationAndTimezoneRequirements job={job} />
          <AvailabilityFieldContainer />
          <Field name="calendar_url" component={CalendarField} />
          <Fields
            names={[
              "legally_authorized_to_work",
              "visa_sponsorship_may_be_required",
            ]}
            component={WorkAuthorizationField}
            validate={[thisFieldIsRequired]}
          />
          <Field
            name="resume"
            component={AddResumeToBidField}
            isRequired={isResumeRequired}
            isEditPage={!!isEditPage}
            validate={!isResumeRequired ? undefined : [thisFieldIsRequired]}
          />
          <FieldSectionPanelWithImportance
            id="attachment"
            title="Would you like to attach any files?"
            fieldImportance="optional"
          >
            <JobNewBidFileDropField existing={existing} />
          </FieldSectionPanelWithImportance>
        </div>

        <div>
          <FieldArray
            name="new_application_answers"
            component={NewApplicationAnswersField}
          />
        </div>
      </div>
    </>
  );
};

type DetailsSubheaderProps = {
  job: Job | null | undefined;
};

const DetailsSubheader = ({ job }: DetailsSubheaderProps) => {
  if (!job) return null;

  return (
    <Box
      sx={{
        py: 2.5,
        px: { xs: 2, md: 7.5 },
        borderBottom: "1px solid var(--grey-4)",
        bgcolor: "var(--off-white)",
        display: "flex",
        justifyContent: "space-between",
        minWidth: "360px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: 1,
          flexWrap: { xs: "wrap", md: "nowrap" },
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: "1rem 2rem", lg: "1rem 4rem" },
          margin: "auto",
          maxWidth: "1320px",
        }}
      >
        <BudgetDetail job={job} variant="details" shortened />
        <LocationDetail locations={job.locations} variant="details" />
        <TimezoneDetail
          timezones={job.timezones}
          timezoneOverlap={job.timezone_overlap}
          variant="details"
        />
        <WorkTimeDetail
          expectedHoursPerWeek={job.expected_hours_per_week}
          variant="details"
          shortened
        />
        <DeadlineDetail job={job} variant="details" />
      </Box>
    </Box>
  );
};
